/**
 * One person/craft row dispatched to a job, shown on the equipment
 * dashboard's dispatched-crew reference panel.
 * Mirrors UPS.Sparta.DTO.OperationsManagement.DispatchedCrewMemberDto.
 * The API serializes PascalCase; consume via a CamelCase-converting client.
 */
export interface DispatchedCrewMemberDto {
  employeeName: string;
  hrRef?: number;
  craft: string;
  shift?: number;
  startDate?: string;
}

/**
 * Vehicle location data from Geotab for a job's team members.
 * Mirrors UPS.Sparta.DTO.OperationsManagement.JobVehicleLocationDto.
 * The API serializes PascalCase; consume via a CamelCase-converting client.
 */
export interface JobVehicleLocationDto {
  employeeName: string;
  hrRef?: number;
  vehicleId: string;
  vehicleName: string;
  vehicleVIN: string;
  latitude?: number;
  longitude?: number;
  locationTimestamp?: string;
  speed?: number;
  status: string;
  direction?: string;
}

/**
 * Response containing all vehicle locations for a job.
 * Mirrors UPS.Sparta.DTO.OperationsManagement.JobVehicleLocationsResponseDto.
 * The API serializes PascalCase; consume via a CamelCase-converting client.
 */
export interface JobVehicleLocationsResponseDto {
  vpJobId: string;
  vehicles: JobVehicleLocationDto[];
  dataAsOf: string;
  totalEmployees: number;
  vehiclesFound: number;
}

/**
 * One fleet vehicle with its current Geotab driver assignment (latest
 * DriverChange record per device) and location.
 * Mirrors UPS.Sparta.DTO.OperationsManagement.FleetVehicleDto.
 * The API serializes PascalCase; consume via a CamelCase-converting client.
 */
export interface FleetVehicleDto {
  deviceId: string;
  vehicleName: string;
  vehicleVIN: string;
  serialNumber?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  driverName?: string;
  /** The assigned driver's HRRef as Geotab stores it (EmployeeNo), stringly. */
  employeeNo?: string;
  latitude?: number;
  longitude?: number;
  locationTimestamp?: string;
  speed?: number;
  bearing?: number;
  status?: string;
  isDeviceCommunicating?: boolean;
}

/**
 * Response containing all fleet vehicles.
 * Mirrors UPS.Sparta.DTO.OperationsManagement.FleetVehiclesResponseDto.
 * The API serializes PascalCase; consume via a CamelCase-converting client.
 */
export interface FleetVehiclesResponseDto {
  vehicles: FleetVehicleDto[];
  dataAsOf: string;
  totalVehicles: number;
  activeVehicles: number;
}

/**
 * One person-to-vehicle assignment from the fleet team's SharePoint
 * "Master Vehicle List.xlsx" — the authoritative assignment source (#30651);
 * Geotab's DriverChange store is empty in production.
 * Mirrors UPS.Sparta.DTO.OperationsManagement.VehicleAssignmentDto.
 * The API serializes PascalCase; consume via a CamelCase-converting client.
 */
export interface VehicleAssignmentDto {
  /** The fleet unit number — the name the fleet team knows the truck by. */
  unitNumber: string;
  /** Viewpoint HRRef of the assigned employee (the sheet's "HR Reference #"). */
  hrRef: number;
  /** Driver display name as maintained on the sheet (display only, never a join key). */
  driverName?: string;
  year?: string;
  make?: string;
  model?: string;
  branch?: string;
  /** The sheet's Geotab device serial, for unit → live-location pairing. */
  geotabSerial?: string;
}

/**
 * Envelope for the vehicle-assignments endpoint. hasError=true means the
 * SharePoint source could not be read and assignments is not trustworthy —
 * render "unknown", not "no vehicles".
 * Mirrors UPS.Sparta.DTO.OperationsManagement.VehicleAssignmentsResponseDto.
 * The API serializes PascalCase; consume via a CamelCase-converting client.
 */
export interface VehicleAssignmentsResponseDto {
  assignments: VehicleAssignmentDto[];
  dataAsOf: string;
  hasError: boolean;
}
